import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider
} from '@mui/material';
import {
  Class as ClassIcon,
  People as PeopleIcon,
  Event as EventIcon,
  CheckCircle as StartWorkIcon,
  CheckCircle,
  ExitToApp as EndWorkIcon,
  ManageAccounts as ManageIcon,
  EventBusy as LeaveIcon,
  Campaign as AnnouncementsIcon,
  AssignmentLate as CareNotesIcon,
  Receipt as ExpensesIcon,
  LibraryBooks as LibraryIcon,
  ChildCare as DaycareIcon,
  Inbox as InboxIcon,
  CalendarMonth as StaffCalIcon,
} from '@mui/icons-material';
import { teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const TeacherDashboard = () => {
  const { user, logout, isFeatureEnabled } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState('');
  const [startingWork, setStartingWork] = useState(false);
  const [pendingCareNotes, setPendingCareNotes] = useState(0);
  const [pendingDaycareNotes, setPendingDaycareNotes] = useState(0);

  const [elapsedTime, setElapsedTime] = useState('');

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await teacherAPI.getDashboard();
      setDashboardData(response.data);
      setError('');
    } catch (error) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCareNoteCount = useCallback(async () => {
    try {
      const res = await teacherAPI.careNotes.today();
      const notes = res.data.notes || [];
      setPendingCareNotes(notes.filter(n => n.status === 'pending').length);
    } catch {
      // non-blocking
    }
  }, []);

  const fetchDaycareNoteCount = useCallback(async () => {
    try {
      const res = await teacherAPI.daycare.getCareNotes();
      const notes = res.data.notes || [];
      setPendingDaycareNotes(notes.filter(n => n.status === 'pending').length);
    } catch {
      // non-blocking
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchCareNoteCount();
  }, [fetchDashboard, fetchCareNoteCount]);

  useEffect(() => {
    if (dashboardData?.is_daycare_teacher) fetchDaycareNoteCount();
  }, [dashboardData?.is_daycare_teacher, fetchDaycareNoteCount]);

  // Update elapsed time every minute
  useEffect(() => {
    const todayAttendance = dashboardData?.stats?.todayAttendance;
    
    if (!todayAttendance?.check_in_time || todayAttendance?.check_out_time) {
      setElapsedTime('');
      return;
    }

    const updateElapsedTime = () => {
      const checkInTime = new Date(todayAttendance.check_in_time);
      const now = new Date();
      const diffMs = now - checkInTime;
      
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        setElapsedTime(`${hours}h ${minutes}m`);
      } else {
        setElapsedTime(`${minutes}m`);
      }
    };

    // Update immediately
    updateElapsedTime();

    // Update every minute
    const interval = setInterval(updateElapsedTime, 60000);

    return () => clearInterval(interval);
  }, [dashboardData]);

  // Removed unused check-in/out helpers to satisfy ESLint (not used in UI)

  const handleStartWork = async () => {
    setStartingWork(true);
    setError('');
    try {
      await teacherAPI.startWork();
      await fetchDashboard();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to start work');
    } finally {
      setStartingWork(false);
    }
  };

  const handleEndWork = async () => {
    setStartingWork(true);
    setError('');
    try {
      await teacherAPI.checkOut();
      await fetchDashboard();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to end work');
    } finally {
      setStartingWork(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const stats = dashboardData?.stats || {};
  const classes = dashboardData?.classes || [];
  const upcomingMeetings = dashboardData?.upcomingMeetings || [];
  const todayAttendance = dashboardData?.stats?.todayAttendance;
  const isDaycareTeacher = dashboardData?.is_daycare_teacher === true;

  return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }} data-testid="teacher-dashboard">

      {/* Attendance Start/End Work */}
      {todayAttendance ? (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.50' }} data-testid="attendance-checked-in">
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2}>
            <Box>
              <Typography variant="h6" color="success.main" data-testid="checked-in-status">
                ✓ Work Started
              </Typography>
              <Typography variant="body2" data-testid="check-in-time">
                Work start time: {new Date(todayAttendance.check_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
              </Typography>
              {elapsedTime && (
                <Typography variant="body2" fontWeight="bold" color="success.dark" sx={{ mt: 0.5 }} data-testid="work-elapsed-time">
                  Work time: {elapsedTime}
                </Typography>
              )}
            </Box>
            {!todayAttendance.check_out_time && (
              <Button
                variant="contained"
                color="warning"
                startIcon={<EndWorkIcon />}
                onClick={handleEndWork}
                disabled={startingWork}
                data-testid="checkout-button"
              >
                End Work
              </Button>
            )}
            {todayAttendance.check_out_time && (
              <Box textAlign="right">
                <Typography variant="h6" color="error.main" data-testid="work-ended-status">
                  ✗ Work Ended
                </Typography>
                <Typography variant="body2" data-testid="check-out-time">
                  Work ended at: {new Date(todayAttendance.check_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ p: 3, mb: 3 }} data-testid="attendance-not-checked-in">
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2}>
            <Typography variant="h6" data-testid="not-checked-in-message">
              You haven't started work today
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<StartWorkIcon />}
              onClick={handleStartWork}
              disabled={startingWork}
              data-testid="checkin-button"
            >
              {startingWork ? <CircularProgress size={24} /> : 'Start Work'}
            </Button>
          </Box>
        </Paper>
      )}

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.50' }} data-testid="error-message">
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4} data-testid="stats-section">
        <Grid item xs={12} sm={6} md={4}>
          <Card
            data-testid="stats-classes"
            elevation={0}
            onClick={() => navigate('/teacher/classes')}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              cursor: 'pointer',
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 25px -5px rgba(102, 126, 234, 0.3)',
              },
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" fontWeight="bold" data-testid="total-classes-count">
                    {stats.totalClasses || 0}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                    Assigned Classes
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ClassIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card
            data-testid="stats-students"
            elevation={0}
            onClick={() => navigate('/teacher/attendance')}
            sx={{
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white',
              cursor: 'pointer',
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 25px -5px rgba(67, 233, 123, 0.3)',
              },
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" fontWeight="bold" data-testid="total-students-count">
                    {stats.totalStudents || 0}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                    Total Students
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card
            data-testid="stats-meetings"
            elevation={0}
            onClick={() => navigate('/teacher/meetings')}
            sx={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              cursor: 'pointer',
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 25px -5px rgba(250, 112, 154, 0.3)',
              },
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" fontWeight="bold" data-testid="upcoming-meetings-count">
                    {stats.upcomingMeetings || 0}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                    Upcoming Meetings
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EventIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Box mb={3}>
        {/* Daily Teaching */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
          Daily Teaching
        </Typography>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={6} lg={4}>
            <Button
              variant="contained"
              startIcon={<ManageIcon />}
              onClick={() => navigate('/teacher/classes')}
              fullWidth
            >
              Manage My Classes
            </Button>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => navigate('/teacher/attendance')}
              fullWidth
            >
              Mark Attendance
            </Button>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<EventIcon />}
              onClick={() => navigate('/teacher/meetings')}
              fullWidth
            >
              Parent Meetings
            </Button>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Button
              variant="contained"
              startIcon={<InboxIcon />}
              onClick={() => navigate('/teacher/inbox')}
              fullWidth
              sx={{ bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}
            >
              Inbox
            </Button>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Button
              variant="contained"
              startIcon={<StaffCalIcon />}
              onClick={() => navigate('/teacher/staff-calendar')}
              fullWidth
              sx={{ bgcolor: '#0891b2', '&:hover': { bgcolor: '#0e7490' } }}
            >
              Staff Calendar
            </Button>
          </Grid>
          {isFeatureEnabled('care_notes') && isDaycareTeacher && (
            <Grid item xs={12} md={6} lg={4}>
              <Button
                variant="contained"
                startIcon={<CareNotesIcon />}
                onClick={() => navigate('/teacher/care-notes')}
                fullWidth
                sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' }, position: 'relative' }}
              >
                Care Notes
                {pendingCareNotes > 0 && (
                  <Chip
                    label={pendingCareNotes}
                    size="small"
                    color="error"
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                  />
                )}
              </Button>
            </Grid>
          )}
          {isDaycareTeacher && (
            <Grid item xs={12} md={6} lg={4}>
              <Button
                variant="contained"
                startIcon={<DaycareIcon />}
                onClick={() => navigate('/teacher/daycare')}
                fullWidth
                sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' }, position: 'relative' }}
              >
                Daycare
                {pendingDaycareNotes > 0 && (
                  <Chip
                    label={pendingDaycareNotes}
                    size="small"
                    color="error"
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                  />
                )}
              </Button>
            </Grid>
          )}
        </Grid>

        {/* Personal Management */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
          Personal Management
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <Button
              variant="contained"
              color="info"
              startIcon={<CheckCircle />}
              onClick={() => navigate('/teacher/analytics')}
              fullWidth
            >
              View Analytics
            </Button>
          </Grid>
          {isFeatureEnabled('leave_management') && (
            <Grid item xs={12} md={6} lg={4}>
              <Button
                variant="contained"
                color="warning"
                startIcon={<LeaveIcon />}
                onClick={() => navigate('/teacher/leave-management')}
                fullWidth
              >
                Leave Management
              </Button>
            </Grid>
          )}
          <Grid item xs={12} md={6} lg={4}>
            <Button
              variant="contained"
              startIcon={<EventIcon />}
              onClick={() => navigate('/teacher/holidays')}
              fullWidth
              sx={{ bgcolor: '#0891B2', '&:hover': { bgcolor: '#0E7490' } }}
            >
              Holiday Calendar
            </Button>
          </Grid>
          {isFeatureEnabled('academics') && (
            <Grid item xs={12} md={6} lg={4}>
              <Button
                variant="contained"
                startIcon={<EventIcon />}
                onClick={() => navigate('/teacher/calendar')}
                fullWidth
                sx={{ bgcolor: '#1976D2', '&:hover': { bgcolor: '#1565C0' } }}
              >
                School Calendar
              </Button>
            </Grid>
          )}
          {isFeatureEnabled('announcements') && (
            <Grid item xs={12} md={6} lg={4}>
              <Button
                variant="contained"
                startIcon={<AnnouncementsIcon />}
                onClick={() => navigate('/teacher/announcements')}
                fullWidth
                sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}
              >
                Announcements
              </Button>
            </Grid>
          )}
          {isFeatureEnabled('expenses') && (
            <Grid item xs={12} md={6} lg={4}>
              <Button
                variant="contained"
                startIcon={<ExpensesIcon />}
                onClick={() => navigate('/teacher/expenses')}
                fullWidth
                sx={{ bgcolor: '#0369A1', '&:hover': { bgcolor: '#0284C7' } }}
              >
                My Expenses
              </Button>
            </Grid>
          )}
          {isFeatureEnabled('library') && (
            <Grid item xs={12} md={6} lg={4}>
              <Button
                variant="contained"
                startIcon={<LibraryIcon />}
                onClick={() => navigate('/teacher/library')}
                fullWidth
                sx={{ bgcolor: '#B45309', '&:hover': { bgcolor: '#92400E' } }}
              >
                Library
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Assigned Classes Section */}
      <Paper sx={{ p: 3, mb: 3 }} data-testid="assigned-classes-section">
        <Typography variant="h6" fontWeight="bold" gutterBottom data-testid="classes-section-title">
          Your Assigned Classes
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {classes.length === 0 ? (
          <Typography color="text.secondary" data-testid="no-classes-message">
            No classes assigned yet
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {classes.map((cls) => (
              <Grid item xs={12} sm={6} md={4} key={cls.id}>
                <Card variant="outlined" data-testid={`class-card-${cls.id}`}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" data-testid={`class-name-${cls.id}`}>
                      {cls.name}
                    </Typography>
                    {cls.grade && (
                      <Typography variant="body2" color="text.secondary" data-testid={`class-grade-${cls.id}`}>
                        Grade: {cls.grade} {cls.section ? `- Section ${cls.section}` : ''}
                      </Typography>
                    )}
                    <Box mt={1}>
                      <Chip
                        label={`${cls.students?.[0]?.count || 0} Students`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        data-testid={`class-students-${cls.id}`}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Upcoming Meetings */}
      {upcomingMeetings.length > 0 && (
        <Paper sx={{ p: 3 }} data-testid="upcoming-meetings-section">
          <Typography variant="h6" fontWeight="bold" gutterBottom data-testid="meetings-section-title">
            Upcoming Parent Meetings
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List data-testid="meetings-list">
            {upcomingMeetings.slice(0, 5).map((meeting, index) => (
              <React.Fragment key={meeting.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={`Meeting with ${meeting.parent_name || 'Parent'}`}
                    secondary={`${new Date(meeting.scheduled_datetime).toLocaleString()} - ${meeting.meeting_agenda || 'No agenda'}`}
                  />
                  <Chip
                    label={meeting.status}
                    size="small"
                    color={meeting.status === 'scheduled' ? 'primary' : 'default'}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
      </Container>
  );
};

export default TeacherDashboard;
